// Opt-in performance probe (docs/PERFORMANCE.md). Off unless the page URL has `?perf` (or
// `?perf=marks`, which also writes `performance.mark/measure` entries for DevTools traces). Off, every
// call below is one predictable branch. On, it records per-frame CPU time of the named sections into
// preallocated ring buffers (no allocation in the frame), GPU time through
// `EXT_disjoint_timer_query_webgl2` when the browser offers it, input send and snapshot arrival
// timestamps, long tasks and long animation frames, and the hit registration check (`hitreg.ts`).
// `window.__gunrunPerf.dump()` and `.hitreg()` return everything as plain data for the harness in
// tools/perf/.

import { TICK_RATE } from "../net/constants";
import { HitregProbe } from "./hitreg";

const params = typeof location === "undefined" ? new URLSearchParams() : new URLSearchParams(location.search);

export const PERF = params.has("perf");
const TRACE_MARKS = params.get("perf") === "marks";

/** Ids of the timed sections (index into the per-frame columns). */
export const Sec = {
  /** The whole frame callback: update + render. */
  frame: 0,
  update: 1,
  render: 2,
  /** update: render tick, remote sampling, projectile timeline. */
  snap: 3,
  /** update: input sampling, prediction ticks, send. */
  local: 4,
  camera: 5,
  remotes: 6,
  events: 7,
  viewmodel: 8,
  hud: 9,
  audio: 10,
  objectives: 11,
  /** render: sky, objective/projectile/effect updates before any draw. */
  rPrep: 12,
  /** render: the draw calls (shadow pass, world, viewmodel, post-processing). */
  rDraw: 13,
  /** render: the full-screen flash overlay. */
  rOverlay: 14,
} as const;

const NAMES = [
  "frame",
  "update",
  "render",
  "u.snap",
  "u.local",
  "u.camera",
  "u.remotes",
  "u.events",
  "u.viewmodel",
  "u.hud",
  "u.audio",
  "u.objectives",
  "r.prep",
  "r.draw",
  "r.overlay",
] as const;

const SECTIONS = NAMES.length;
/** Frames kept (about 4.5 minutes at 60 fps). */
const CAP = 16384;
/** Timestamps kept for input sends and snapshot arrivals: 9 minutes of snapshots (one per tick; inputs leave at most about as often). */
const EVENT_CAP = 9 * 60 * TICK_RATE;

interface TaskEntry {
  start: number;
  duration: number;
}

interface FrameEntry extends TaskEntry {
  blocking: number;
  renderStart: number;
  styleAndLayoutStart: number;
  scripts: { invoker: string; fn: string; url: string; duration: number; forcedLayout: number }[];
}

interface GpuQuery {
  query: WebGLQuery;
  frame: number;
}

interface TimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

interface LoafScript {
  invoker: string;
  sourceFunctionName: string;
  sourceURL: string;
  duration: number;
  forcedStyleAndLayoutDuration: number;
}

type LoafEntry = PerformanceEntry & {
  blockingDuration: number;
  renderStart: number;
  styleAndLayoutStart: number;
  scripts: LoafScript[];
};

/** `long-animation-frame` entries are not in the DOM typings yet. */
function isLoaf(e: PerformanceEntry): e is LoafEntry {
  return "blockingDuration" in e && "scripts" in e && Array.isArray(e.scripts);
}

function isTimerExt(ext: unknown): ext is TimerExt {
  return typeof ext === "object" && ext !== null && "TIME_ELAPSED_EXT" in ext && "GPU_DISJOINT_EXT" in ext;
}

class Probe {
  readonly cols: Float32Array[] = [];
  private readonly starts = new Float64Array(SECTIONS);
  private readonly acc = new Float64Array(SECTIONS);
  /** Wall time of the frame start (`performance.now()`), for aligning with long tasks. */
  readonly at = new Float64Array(CAP);
  /** Frame interval (ms) as the frame loop saw it. */
  readonly dt = new Float32Array(CAP);
  readonly calls = new Float32Array(CAP);
  readonly triangles = new Float32Array(CAP);
  readonly gpu = new Float32Array(CAP).fill(-1);
  n = 0;
  private frameStarted = 0;
  private lastFrameAt = 0;

  readonly inputSends = new Float64Array(EVENT_CAP);
  inputSendCount = 0;
  readonly snapshotArrivals = new Float64Array(EVENT_CAP);
  readonly snapshotTicks = new Float64Array(EVENT_CAP);
  snapshotCount = 0;
  readonly longTasks: TaskEntry[] = [];
  readonly frames: FrameEntry[] = [];
  readonly marks: { name: string; t: number }[] = [];
  readonly objects: Record<string, unknown> = {};
  /** Shown hits of the local player against the server's `Hit`s (`hitreg.ts`). */
  readonly hitreg = new HitregProbe();

  private gl: WebGL2RenderingContext | null = null;
  private timer: TimerExt | null = null;
  private pending: GpuQuery[] = [];
  private open: GpuQuery | null = null;
  private pool: WebGLQuery[] = [];
  gpuSupported = false;
  gpuDisjoint = 0;
  private info: (() => { calls: number; triangles: number }) | null = null;
  private resources: (() => Record<string, number>) | null = null;

  constructor() {
    for (let i = 0; i < SECTIONS; i++) this.cols.push(new Float32Array(CAP));
    this.observe();
  }

  private observe(): void {
    if (typeof PerformanceObserver === "undefined") return;
    const watch = (type: string, handle: (e: PerformanceEntry) => void): void => {
      try {
        new PerformanceObserver((list) => list.getEntries().forEach(handle)).observe({ type, buffered: true });
      } catch {
        // Entry type not supported by this browser.
      }
    };
    watch("longtask", (e) => this.longTasks.push({ start: e.startTime, duration: e.duration }));
    watch("long-animation-frame", (e) => {
      if (!isLoaf(e)) return;
      this.frames.push({
        start: e.startTime,
        duration: e.duration,
        blocking: e.blockingDuration,
        renderStart: e.renderStart,
        styleAndLayoutStart: e.styleAndLayoutStart,
        scripts: e.scripts.map((s) => ({
          invoker: s.invoker,
          fn: s.sourceFunctionName,
          url: s.sourceURL,
          duration: s.duration,
          forcedLayout: s.forcedStyleAndLayoutDuration,
        })),
      });
    });
  }

  /** The renderer hands over its context (GPU timer) and counters. */
  attach(
    gl: WebGL2RenderingContext,
    info: () => { calls: number; triangles: number },
    resources: () => Record<string, number>,
  ): void {
    this.gl = gl;
    this.info = info;
    this.resources = resources;
    const ext: unknown = gl.getExtension("EXT_disjoint_timer_query_webgl2");
    this.timer = isTimerExt(ext) ? ext : null;
    this.gpuSupported = this.timer !== null;
  }

  /** Hands a live object to the page's `__gunrunPerf.objects` for the harness to inspect. */
  expose(name: string, value: unknown): void {
    this.objects[name] = value;
  }

  mark(name: string): void {
    this.marks.push({ name, t: performance.now() });
    if (TRACE_MARKS) performance.mark(name);
  }

  begin(id: number): void {
    this.starts[id] = performance.now();
  }

  end(id: number): void {
    this.acc[id] += performance.now() - this.starts[id];
  }

  frameStart(nowMs: number): void {
    this.acc.fill(0);
    this.frameStarted = performance.now();
    this.dt[this.n % CAP] = this.lastFrameAt > 0 ? nowMs - this.lastFrameAt : 0;
    this.lastFrameAt = nowMs;
    this.at[this.n % CAP] = this.frameStarted;
    if (TRACE_MARKS) performance.mark("frame-start");
  }

  frameEnd(): void {
    const i = this.n % CAP;
    this.acc[Sec.frame] = performance.now() - this.frameStarted;
    for (let s = 0; s < SECTIONS; s++) this.cols[s][i] = this.acc[s];
    const info = this.info?.();
    this.calls[i] = info?.calls ?? 0;
    this.triangles[i] = info?.triangles ?? 0;
    this.gpu[i] = -1;
    this.n++;
    this.pollGpu();
    if (TRACE_MARKS) performance.measure("frame", "frame-start");
  }

  gpuBegin(): void {
    const gl = this.gl;
    const timer = this.timer;
    if (!gl || !timer || this.open || this.pending.length >= 6) return;
    const query = this.pool.pop() ?? gl.createQuery();
    gl.beginQuery(timer.TIME_ELAPSED_EXT, query);
    this.open = { query, frame: this.n };
  }

  gpuEnd(): void {
    const gl = this.gl;
    const timer = this.timer;
    const open = this.open;
    if (!gl || !timer || !open) return;
    gl.endQuery(timer.TIME_ELAPSED_EXT);
    this.pending.push(open);
    this.open = null;
  }

  private pollGpu(): void {
    const gl = this.gl;
    const timer = this.timer;
    if (!gl || !timer) return;
    while (this.pending.length > 0) {
      const q = this.pending[0];
      if (!gl.getQueryParameter(q.query, gl.QUERY_RESULT_AVAILABLE)) break;
      this.pending.shift();
      const disjoint = Boolean(gl.getParameter(timer.GPU_DISJOINT_EXT));
      if (disjoint) this.gpuDisjoint++;
      else if (q.frame > this.n - CAP) this.gpu[q.frame % CAP] = Number(gl.getQueryParameter(q.query, gl.QUERY_RESULT)) / 1e6;
      this.pool.push(q.query);
    }
  }

  recordInputSend(nowMs: number): void {
    this.inputSends[this.inputSendCount % EVENT_CAP] = nowMs;
    this.inputSendCount++;
  }

  recordSnapshot(nowMs: number, tick: number): void {
    const i = this.snapshotCount % EVENT_CAP;
    this.snapshotArrivals[i] = nowMs;
    this.snapshotTicks[i] = tick;
    this.snapshotCount++;
  }

  /** Forgets the frames and events recorded so far (the marks and long tasks stay). */
  reset(): void {
    this.n = 0;
    this.lastFrameAt = 0;
    this.inputSendCount = 0;
    this.snapshotCount = 0;
    this.gpu.fill(-1);
    this.hitreg.reset();
  }

  private tail(source: Float32Array | Float64Array, count: number): number[] {
    const kept = Math.min(count, source.length);
    const out = new Array<number>(kept);
    for (let k = 0; k < kept; k++) out[k] = source[(count - kept + k) % source.length];
    return out;
  }

  dump(): unknown {
    const kept = Math.min(this.n, CAP);
    const sections: Record<string, number[]> = {};
    for (let s = 0; s < SECTIONS; s++) sections[NAMES[s]] = this.tail(this.cols[s], this.n);
    return {
      frames: kept,
      sections,
      at: this.tail(this.at, this.n),
      dt: this.tail(this.dt, this.n),
      calls: this.tail(this.calls, this.n),
      triangles: this.tail(this.triangles, this.n),
      gpu: this.tail(this.gpu, this.n),
      gpuSupported: this.gpuSupported,
      gpuDisjoint: this.gpuDisjoint,
      inputSends: this.tail(this.inputSends, this.inputSendCount),
      snapshotArrivals: this.tail(this.snapshotArrivals, this.snapshotCount),
      snapshotTicks: this.tail(this.snapshotTicks, this.snapshotCount),
      longTasks: this.longTasks,
      longAnimationFrames: this.frames,
      marks: this.marks,
      resources: this.resources?.() ?? {},
      now: performance.now(),
    };
  }
}

export const probe: Probe | null = PERF ? new Probe() : null;

// ---- Call-site helpers: one branch when the probe is off ----

export function perfBegin(id: number): void {
  if (probe) probe.begin(id);
}

export function perfEnd(id: number): void {
  if (probe) probe.end(id);
}

export function perfMark(name: string): void {
  if (probe) probe.mark(name);
}

if (probe && typeof window !== "undefined") {
  Object.assign(window, {
    __gunrunPerf: {
      dump: () => probe.dump(),
      hitreg: () => probe.hitreg.dump(),
      reset: () => probe.reset(),
      mark: (name: string) => probe.mark(name),
      objects: probe.objects,
    },
  });
}
